import ListAgreement from '../../models/general/agreements/list-agreement';
import CurrentUser, {
  RobotSubscriptions,
  OrganizationSubscriptions,
} from '../../models/general/current-user';
import LoginDetails, { LoginResponse } from '../../models/general/login-details';
import SetPasswordDetails from '../../models/general/set-password-details';
import Filters, { customCreateSearchParams } from '@common/enums/query-params/filters';
import { currentVersionManagement, managementApiInstance } from './axios-base';
import AgreementDetails from '../../models/general/agreements/agreement-details';
import AcceptanceForm from '../../models/general/agreements/acceptance-form';
import Acceptance from '../../models/general/agreements/acceptance';
import CustomLocalization from '../../models/general/custom-localizations/custom-localization';
import NotificationSubscription from '../../models/general/notifications/notification-trigger';
import UpdateVersion from '../../models/general/versions/update-version';
import FormUser from '../../models/general/form-user';
import FrontendParams from '../../models/general/frontend-params';
import ReportParameters from '../../models/general/notifications/report-parameters';
import ReportDataSet from '../../models/general/notifications/report-data-set';
import MissionRequestFilters from '../../models/service/stats/mission-request-filters';
import { StatsMissionResponse } from '../../models/service/stats/mission-set';
import VersionSet from '../../models/general/versions/version-set';
import TorizonPackage from '../../models/general/versions/torizon-package';
import TorizonVersionSet from '../../models/general/versions/torizon-version-set';
import AssignableRobot from '../../models/general/versions/assignable-robot';
import Announcement from '../../models/service/announcements/announcement';
import {
  TwoFactorSetupInitResponse,
  TwoFactorSetupConfirmResponse,
  VerifyTwoFactorRequest,
} from '../../models/general/two-factor';

class GeneralDataService {
  async login(loginDetails: Partial<LoginDetails>): Promise<LoginResponse> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/login/`,
      loginDetails,
    );
    return response.data;
  }

  async postResetPasswordEmail(email: string): Promise<void> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/reset-password/`,
      { email },
    );
    return response.data;
  }

  async unsubscribe(token: string): Promise<{ status: string }> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/unsubscribe/?token=${encodeURIComponent(token)}`,
    );
    return response.data;
  }

  async setPassword(data: SetPasswordDetails): Promise<LoginResponse> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/set-password/`,
      data,
    );
    return response.data;
  }

  async getUserByToken(): Promise<CurrentUser> {
    const response = await managementApiInstance.get(`/${currentVersionManagement}/users/token/`);
    return response.data;
  }

  async getAgreements(): Promise<ListAgreement[]> {
    const response = await managementApiInstance.get(`/${currentVersionManagement}/agreements/`);
    return response.data;
  }

  async getAgreementById(id: string): Promise<AgreementDetails> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/agreements/${id}/`,
    );
    return response.data;
  }

  async createAgreement(data: Partial<ListAgreement>): Promise<ListAgreement> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/agreements/`,
      data,
    );
    return response.data;
  }

  async patchAgreementById(id: string, data: Partial<ListAgreement>): Promise<ListAgreement> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/agreements/${id}/`,
      data,
    );
    return response.data;
  }

  async deleteAgreementById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/agreements/${id}/`,
    );
    return response.data;
  }

  async postUploadAgreementFile(
    updatePercentComplete: (percentComplete: number) => void,
    formData: FormData,
  ): Promise<AgreementDetails> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/agreements/${formData.get('agreementId')}/files/`,
      formData,
      {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent?.total ?? 1),
          );
          updatePercentComplete(percentCompleted);
        },
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return response.data;
  }

  async deleteAgreementFileById(agreementId: string, fileId: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/agreements/${agreementId}/files/${fileId}/`,
    );
    return response.data;
  }

  async createAgreementAcceptance(data: Partial<AcceptanceForm>): Promise<Acceptance> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/agreements/${data.agreementId}/accept/`,
      data,
    );
    return response.data;
  }

  async getCustomLocalizations(): Promise<CustomLocalization[]> {
    const response = await managementApiInstance.get(`/${currentVersionManagement}/localizations/`);
    return response.data;
  }

  async getCustomLocalizationById(id: string): Promise<CustomLocalization> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/localizations/${id}/`,
    );
    return response.data;
  }

  async createCustomLocalization(data: Partial<CustomLocalization>): Promise<CustomLocalization> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/localizations/`,
      data,
    );
    return response.data;
  }

  async patchCustomLocalizationById(
    id: string,
    data: Partial<CustomLocalization>,
  ): Promise<CustomLocalization> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/localizations/${id}/`,
      data,
    );
    return response.data;
  }

  async deleteCustomLocalizationById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/localizations/${id}/`,
    );
    return response.data;
  }

  async getNotificationSubscriptions(): Promise<NotificationSubscription[]> {
    const response = await managementApiInstance.get(`/${currentVersionManagement}/notifications/`);
    return response.data;
  }

  async createNotificationSubscription(
    data: Partial<NotificationSubscription>,
  ): Promise<NotificationSubscription> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/notifications/`,
      data,
    );
    return response.data;
  }

  async patchNotificationSubscriptionById(
    id: string,
    data: Partial<NotificationSubscription>,
  ): Promise<NotificationSubscription> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/notifications/${id}/`,
      data,
    );
    return response.data;
  }

  async deleteNotificationSubscriptionById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/notifications/${id}/`,
    );
    return response.data;
  }

  async registerNotificationDevice(token: string): Promise<string> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/register-device/`,
      { token },
    );
    return response.data;
  }

  async getUpdateVersions(): Promise<UpdateVersion[]> {
    const response = await managementApiInstance.get(`/${currentVersionManagement}/versions/`);
    return response.data;
  }

  async getTorizonPackages(): Promise<TorizonPackage[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/torizon/packages/`,
    );
    return response.data;
  }

  async createUpdateVersion(data: Partial<UpdateVersion>): Promise<UpdateVersion> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/versions/`,
      data,
    );
    return response.data;
  }

  async patchUpdateVersionById(id: string, data: Partial<UpdateVersion>): Promise<UpdateVersion> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/versions/${id}/`,
      data,
    );
    return response.data;
  }

  async deleteUpdateVersionById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/versions/${id}/`,
    );
    return response.data;
  }

  async getVersionSets(): Promise<VersionSet[]> {
    const response = await managementApiInstance.get(`/${currentVersionManagement}/version-sets/`);
    return response.data;
  }

  async createVersionSet(data: Partial<VersionSet>): Promise<VersionSet> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/version-sets/`,
      data,
    );
    return response.data;
  }

  async patchVersionSetById(id: string, data: Partial<VersionSet>): Promise<VersionSet> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/version-sets/${id}/`,
      data,
    );
    return response.data;
  }

  async deleteVersionSetById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/version-sets/${id}/`,
    );
    return response.data;
  }

  async getTorizonVersionSets(): Promise<TorizonVersionSet[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/torizon-version-sets/`,
    );
    return response.data;
  }

  async createTorizonVersionSet(data: Partial<TorizonVersionSet>): Promise<TorizonVersionSet> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/torizon-version-sets/`,
      data,
    );
    return response.data;
  }

  async patchTorizonVersionSetById(
    id: string,
    data: Partial<TorizonVersionSet>,
  ): Promise<TorizonVersionSet> {
    const response = await managementApiInstance.patch(
      `/${currentVersionManagement}/torizon-version-sets/${id}/`,
      data,
    );
    return response.data;
  }

  async deleteTorizonVersionSetById(id: string): Promise<string> {
    const response = await managementApiInstance.delete(
      `/${currentVersionManagement}/torizon-version-sets/${id}/`,
    );
    return response.data;
  }

  async getAssignableRobots(): Promise<AssignableRobot[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/torizon-version-sets/assignable-robots/`,
    );
    return response.data;
  }

  async assignTorizonVersionSet(id: string, robotIds: string[]): Promise<{ assigned: number }> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/torizon-version-sets/${id}/assign/`,
      { robotIds },
    );
    return response.data;
  }

  async validateInvite(filters: Filters): Promise<{ organizationId: string }> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/register/?${customCreateSearchParams(filters)}`,
    );
    return response.data;
  }

  async registerUser(data: Partial<FormUser>, filters: Filters): Promise<void> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/register/?${customCreateSearchParams(filters)}`,
      data,
    );
    return response.data;
  }

  async getFrontendParams(): Promise<FrontendParams> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/frontend-params/`,
    );
    return response.data;
  }

  async postSubscribeToRobotById(id: string): Promise<RobotSubscriptions[]> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/robots/${id}/subscribe/`,
    );
    return response.data;
  }

  async postSubscribeToOrganizationById(id: string): Promise<OrganizationSubscriptions[]> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/organizations/${id}/subscribe/`,
    );
    return response.data;
  }

  async getReportDataSets(params: ReportParameters): Promise<ReportDataSet[]> {
    const response = await managementApiInstance.get(
      `/${currentVersionManagement}/report-data/?${customCreateSearchParams(params)}`,
    );
    return response.data;
  }

  async getStatsMissionEntries(
    requestFilters: MissionRequestFilters,
  ): Promise<StatsMissionResponse> {
    const response = await managementApiInstance.get<StatsMissionResponse>(
      `/${currentVersionManagement}/service/stats/missions?${customCreateSearchParams(requestFilters)}`,
      { timeout: 60000 }, // allow 60 second timeout on this call
    );
    return response.data;
  }

  async getAnnouncements(): Promise<Announcement[]> {
    const response = await managementApiInstance.get<Announcement[]>(
      `/${currentVersionManagement}/announcements`,
    );
    return response.data;
  }

  async verifyTwoFactor(
    data: VerifyTwoFactorRequest,
  ): Promise<{ token: string; user: CurrentUser }> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/verify-2fa/`,
      data,
    );
    return response.data;
  }

  async initTwoFactorSetup(pendingToken?: string): Promise<TwoFactorSetupInitResponse> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/2fa/setup/init/`,
      {
        pendingToken,
      },
    );
    return response.data;
  }

  async confirmTwoFactorSetup(
    otp: string,
    pendingToken?: string,
  ): Promise<TwoFactorSetupConfirmResponse> {
    const response = await managementApiInstance.post(
      `/${currentVersionManagement}/2fa/setup/confirm/`,
      {
        otp,
        pendingToken,
      },
    );
    return response.data;
  }

  async cancelTwoFactorSetup(): Promise<void> {
    await managementApiInstance.post(`/${currentVersionManagement}/2fa/setup/cancel/`, {});
  }

  async disableTwoFactor(
    currentPassword?: string,
    googleToken?: string,
    otp?: string,
    backupCode?: string,
  ): Promise<void> {
    await managementApiInstance.post(`/${currentVersionManagement}/2fa/disable/`, {
      currentPassword,
      googleToken,
      otp,
      backupCode,
    });
  }

  async changePassword(
    currentPassword: string | undefined,
    googleToken: string | undefined,
    newPassword: string,
    otp?: string,
  ): Promise<void> {
    await managementApiInstance.post(`/${currentVersionManagement}/change-password/`, {
      currentPassword,
      googleToken,
      newPassword,
      otp,
    });
  }

  async resetUserTwoFactor(userId: string): Promise<void> {
    await managementApiInstance.post(`/${currentVersionManagement}/users/${userId}/reset-2fa/`, {});
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new GeneralDataService();
